import type { CSSProperties } from 'react';
import type { CrewRow, DisplaySummaryDto, StandingRow } from '@shared/types';
import { initials, localDate, localDateTime, titleCase } from '@shared/format';
import { Header } from './Header';

const fmt = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 2 });

function RankIcon({ rank }: { rank: number }) {
  if (rank === 1) return <svg className="rank-ico gold" viewBox="0 0 24 24" aria-label="First place"><path d="M3 18h18l-1.5-10-4.5 4-3-7-3 7-4.5-4z" /></svg>;
  if (rank <= 3) return <span className={`rank-medal r${rank}`} aria-label={`Rank ${rank}`}>{rank}</span>;
  return <span className="rank-num">{rank}</span>;
}

/** "Ashley Lopez" -> "Ashley L." so the manager fits beside the employee name. */
function shortName(n: string): string {
  const parts = n.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]![0]}.` : n;
}

function PeopleList({ title, rows, tone, empty }: { title: string; rows: StandingRow[]; tone: 'top' | 'bottom'; empty: string }) {
  return (
    <section className={`board board-${tone}`} aria-label={title}>
      <h3>{title}</h3>
      {rows.length === 0 ? <p className="board-empty">{empty}</p> : (
        <ol>
          {rows.map((r, i) => (
            <li key={`${r.displayName}-${i}`} className={tone === 'top' && r.rank === 1 ? 'top' : ''}>
              {tone === 'top' ? <RankIcon rank={r.rank} /> : <span className="rank-num">{r.rank}</span>}
              <span className="name"><span className="emp">{r.displayName}</span>{r.managerName && <em className="mgr" title={r.managerName}>{shortName(r.managerName)}</em>}</span>
              <span className="tokens tabular">{fmt(r.points)}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function CrewBoard({ crews, max, final }: { crews: CrewRow[]; max: number | null; final?: boolean }) {
  const twoCol = crews.length > 9;
  return (
    <section className="crews" aria-label="Crew standings">
      <div className="crews-head">
        <h3>{final ? "Last Week's Final Crew Standings" : 'Crew Standings'}</h3>
        <span className="crews-sub">{max ? `Percent of maximum points (${fmt(max)} per person)` : 'Average points per person'}</span>
      </div>
      {crews.length === 0 ? <p className="board-empty">Crews will show here once foremen are set in Connecteam.</p> : (
        <ol className={`crew-list ${twoCol ? 'crew-two' : ''}`} style={twoCol ? ({ '--crew-rows': Math.ceil(crews.length / 2) } as CSSProperties) : undefined}>
          {crews.map((c) => {
            const pct = max ? Math.max(0, Math.min(100, c.percentOfMax)) : 0;
            return (
              <li key={c.foremanName} className={`crew-row ${c.rank === 1 ? 'crew-lead' : ''}`}>
                <RankIcon rank={c.rank} />
                <div className="crew-main">
                  <div className="crew-line">
                    <span className="crew-name">{c.foremanName}'s Crew</span>
                    <span className="crew-meta tabular">{c.members} {c.members === 1 ? 'person' : 'people'}{max ? ` • ${fmt(c.points)} / ${fmt(c.maxPoints)}` : ''}</span>
                  </div>
                  {max ? <div className="crew-bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></div> : null}
                </div>
                <span className="crew-pct tabular">{max ? `${fmt(c.percentOfMax)}%` : fmt(c.percentOfMax)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

export function LeaderboardScreen({ summary }: { summary: DisplaySummaryDto | null }) {
  const next = summary?.nextDrawing;
  const winner = summary?.recentWinner;
  const weeklyThreshold = summary?.thresholds?.weekly;
  const required = summary?.weeklyRequiredForms ?? [];
  const s = summary?.standings;

  return (
    <div className="screen screen-board">
      <Header />
      {!summary || !s ? (
        <div className="loading">Loading leaderboard…</div>
      ) : (
        <div className="board-layout">
          <div className="people-col">
            <PeopleList title={s.reference === 'previous' ? `Final Top 10 • ${s.label.replace('Week of ', 'Wk of ')}` : `Top 10 • ${s.label}`} rows={s.top} tone="top"
              empty={s.reference === 'previous' ? 'No points last week.' : 'No points yet this week.'} />
            <PeopleList title="Bottom 10" rows={s.bottom} tone="bottom" empty="Everyone is on the board." />
          </div>
          <div className="right-col">
            <CrewBoard crews={s.crews} max={s.maxPointsPerPerson} final={s.reference === 'previous'} />
            <footer className="panels">
              <div className="panel">
                <div className="panel-label">
                  <svg viewBox="0 0 24 24" className="panel-ico gold"><path d="M7 4h10v3a5 5 0 0 1-10 0zM5 5H3v2a4 4 0 0 0 4 4M19 5h2v2a4 4 0 0 1-4 4M9 14h6l1 4H8zM7 20h10" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
                  Recent Winner
                </div>
                {winner ? (
                  <div className="winner-row">
                    <span className="avatar">{initials(winner.winnerName)}</span>
                    <div>
                      <div className="panel-big">{winner.winnerName}</div>
                      <div className="panel-sub">{titleCase(winner.drawingType)} • {localDate(winner.completedAtUtc)} • {winner.prizeName}</div>
                    </div>
                  </div>
                ) : <div className="panel-sub">First winner coming soon.</div>}
              </div>
              <div className="panel panel-wide">
                <div className="panel-label">
                  <svg viewBox="0 0 24 24" className="panel-ico"><rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" /></svg>
                  Next Drawing
                </div>
                {next ? (
                  <div className="panel-big">{next.scheduledForUtc ? localDateTime(next.scheduledForUtc) : `${titleCase(next.drawingType)} drawing coming up`}
                    <span className="panel-sub"> • {next.prizeName}</span></div>
                ) : <div className="panel-sub">Stay accountable: every safety form earns points.</div>}
                {weeklyThreshold !== undefined && (
                  <div className="panel-note">
                    To qualify this week: earn {weeklyThreshold} point{weeklyThreshold === 1 ? '' : 's'}
                    {required.length > 0 && <> and complete the <strong>{required.join(' and ')}</strong></>}.
                  </div>
                )}
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
